import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';

import Modal from '../../../Components/UI/Modal/Modal';
import QuoteCostEditor, { quoteFromEditorValue } from '../../../Components/Project/QuoteCostEditor';
import QuoteRevenuePreview from '../../../Components/Project/QuoteRevenuePreview';
import { emptyMaterialLine, LABOUR_MODES } from '../../../Domain/quote';
import { sendContract } from '../../../Store/Actions/actionOffer';
import { useI18n } from '../../../i18n/I18nProvider';

const todayInputValue = () => {
  const today = new Date();
  const local = new Date(today.getTime() - today.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const initialCostState = (taxMode = '') => ({
  materials: [emptyMaterialLine()],
  labourMode: LABOUR_MODES.HOURS,
  labourHours: '',
  hourlyRate: '',
  wage: '',
  travelCost: '0',
  taxMode,
});

const ContractModal = ({ showModal, handleClick, demand, firebase, loading, sendContractAction }) => {
  const { pick } = useI18n();
  const profile = firebase?.profile || {};
  const template = profile.document_template || {};
  const userId = firebase?.auth?.uid;
  const opened = showModal === 'open';

  const defaults = useMemo(() => ({
    phone: profile.phone_number || '',
    businessName: template.business_name || profile.business_name || profile.company_name || [profile.firstName, profile.lastName].filter(Boolean).join(' '),
    siret: template.siret || profile.siret || '',
    insurance: template.professional_insurance || profile.professional_insurance || profile.insurance || '',
    taxMode: template.default_tax_mode || profile.tax_mode || '',
    validityDays: String(template.quote_validity_days || 30),
    paymentTerms: template.payment_terms || 'Acompte de 20 % à l’acceptation du devis. Solde après confirmation de la fin des travaux par le client.',
  }), [
    profile.business_name,
    profile.company_name,
    profile.firstName,
    profile.insurance,
    profile.lastName,
    profile.phone_number,
    profile.professional_insurance,
    profile.siret,
    profile.tax_mode,
    template.business_name,
    template.default_tax_mode,
    template.payment_terms,
    template.professional_insurance,
    template.quote_validity_days,
    template.siret,
  ]);

  const [description, setDescription] = useState('');
  const [phone, setPhone] = useState('');
  const [startDate, setStartDate] = useState('');
  const [estimatedDuration, setEstimatedDuration] = useState('');
  const [validityDays, setValidityDays] = useState('30');
  const [businessName, setBusinessName] = useState('');
  const [siret, setSiret] = useState('');
  const [insurance, setInsurance] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [costs, setCosts] = useState(initialCostState());
  const [editorSession, setEditorSession] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [showCostValidation, setShowCostValidation] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!opened) return;
    setDescription('');
    setPhone(defaults.phone);
    setStartDate('');
    setEstimatedDuration('');
    setValidityDays(defaults.validityDays);
    setBusinessName(defaults.businessName);
    setSiret(defaults.siret);
    setInsurance(defaults.insurance);
    setPaymentTerms(defaults.paymentTerms);
    const nextCosts = initialCostState(defaults.taxMode);
    setCosts(nextCosts);
    setEditorSession((session) => session + 1);
    setShowCostValidation(false);
    setError('');
  }, [opened, demand?.demand_id]); // Deliberately do not reset a draft on live profile refreshes.

  // The same costs object controls the editor, revenue preview and submit payload.
  const quote = useMemo(() => quoteFromEditorValue(costs), [costs]);

  const close = () => { if (!submitting) handleClick?.(); };

  const submit = async () => {
    if (submitting) return;
    setShowCostValidation(true);
    if (!userId || !demand?.demand_id) return setError(pick('La session ou la demande est introuvable.', 'The session or request could not be found.'));
    if (description.trim().length < 10) return setError(pick('Décrivez la prestation en au moins 10 caractères.', 'Describe the work in at least 10 characters.'));
    if (!phone.trim() || !startDate || !estimatedDuration.trim()) return setError(pick('Renseignez le téléphone, la date de début et la durée estimée.', 'Enter the phone number, start date and estimated duration.'));
    if (!Number.isInteger(Number(validityDays)) || Number(validityDays) < 1 || Number(validityDays) > 365) return setError(pick('La durée de validité doit être comprise entre 1 et 365 jours.', 'Quote validity must be between 1 and 365 days.'));
    if (!quote.valid) return setError(quote.errors[0] || pick('Vérifiez le détail des coûts.', 'Check the cost breakdown.'));

    setError('');
    setSubmitting(true);
    try {
      const success = await sendContractAction({
        demandId: demand.demand_id,
        artisanId: userId,
        artisanFirstName: profile.firstName,
        artisanLastName: profile.lastName,
        artisanEmail: profile.email,
        artisanAddress: profile.address,
        artisanProfileImage: profile.profile_image,
        artisanBusinessName: businessName.trim(),
        artisanSiret: siret.replace(/\s/g, ''),
        artisanInsurance: insurance.trim(),
        artisanCompanyLogo: template.logo_url || '',
        artisanLegalForm: template.legal_form || '',
        artisanVatNumber: template.vat_number || '',
        artisanWebsite: template.website || '',
        paymentTerms: paymentTerms.trim(),
        documentFooter: template.footer_note || '',
        documentTemplateVersion: template.version || 'legacy',
        descriptionOfContract: description.trim(),
        startDate,
        estimatedDuration: estimatedDuration.trim(),
        quoteValidityDays: Number(validityDays),
        contactNumber: phone.trim(),
        materialList: quote.materialList,
        materialCost: quote.materialCost,
        labourMode: quote.labourMode,
        labourHours: quote.labourHours,
        hourlyRate: quote.hourlyRate,
        wage: quote.wage,
        travelCost: quote.travelCost,
        subtotalHt: quote.subtotalHt,
        vatRate: quote.vatRate,
        vatAmount: quote.vatAmount,
        taxLabel: quote.taxLabel,
        totalCost: quote.totalTtc,
      });
      if (success) close();
    } finally {
      setSubmitting(false);
    }
    return undefined;
  };

  return (
    <Modal opened={opened} close={close}>
      <Header>
        <div>
          <Eyebrow>{pick('Après la visite', 'After the assessment')}</Eyebrow>
          <Title>{pick('Préparer le devis définitif', 'Prepare the final quote')}</Title>
          <Subtitle>{pick('Le total se calcule automatiquement et sera recalculé une seconde fois par le serveur avant enregistrement.', 'The total is calculated automatically and recalculated by the server before it is saved.')}</Subtitle>
        </div>
        <CloseButton type="button" aria-label={pick('Fermer', 'Close')} onClick={close}>×</CloseButton>
      </Header>

      <ProfessionalSection>
        <SectionHeader>
          <div><h3>{pick('Informations professionnelles', 'Business information')}</h3><p>{pick('Préremplies depuis votre profil artisan.', 'Pre-filled from your artisan profile.')}</p></div>
          {template.logo_url && <TemplateLogo src={template.logo_url} alt="" />}
        </SectionHeader>
        <Form>
          <Field><label htmlFor="contract-business">{pick('Nom commercial / entreprise', 'Business name')}</label><input id="contract-business" value={businessName} onChange={(event) => setBusinessName(event.target.value)} /></Field>
          <Field><label htmlFor="contract-siret">SIRET</label><input id="contract-siret" value={siret} onChange={(event) => setSiret(event.target.value)} inputMode="numeric" maxLength={18} /></Field>
          <Field $wide><label htmlFor="contract-insurance">{pick('Assurance professionnelle', 'Professional insurance')}</label><input id="contract-insurance" value={insurance} onChange={(event) => setInsurance(event.target.value)} /></Field>
        </Form>
      </ProfessionalSection>

      <Form>
        <Field $wide><label htmlFor="contract-description">{pick('Description précise de la prestation', 'Detailed scope of work')}</label><textarea id="contract-description" value={description} onChange={(event) => setDescription(event.target.value)} maxLength={2000} /><Hint>{description.length}/2000</Hint></Field>
        <Field><label htmlFor="contract-phone">{pick('Téléphone', 'Phone')}</label><input id="contract-phone" type="tel" value={phone} onChange={(event) => setPhone(event.target.value)} /></Field>
        <Field><label htmlFor="contract-date">{pick('Date de début prévue', 'Planned start date')}</label><input id="contract-date" type="date" value={startDate} min={todayInputValue()} onChange={(event) => setStartDate(event.target.value)} /></Field>
        <Field><label htmlFor="contract-duration">{pick('Durée estimée', 'Estimated duration')}</label><input id="contract-duration" value={estimatedDuration} onChange={(event) => setEstimatedDuration(event.target.value)} placeholder={pick('Ex. 3 jours ouvrés', 'E.g. 3 working days')} /></Field>
        <Field><label htmlFor="contract-validity">{pick('Validité du devis (jours)', 'Quote validity (days)')}</label><input id="contract-validity" type="number" min="1" max="365" value={validityDays} onChange={(event) => setValidityDays(event.target.value)} /></Field>
        <Field $wide><label htmlFor="contract-payment-terms">{pick('Conditions de paiement', 'Payment terms')}</label><textarea id="contract-payment-terms" value={paymentTerms} onChange={(event) => setPaymentTerms(event.target.value)} maxLength={500} /></Field>
      </Form>

      <QuoteCostEditor key={editorSession} value={costs} onChange={setCosts} showValidation={showCostValidation} />

      <ClientTransparency>
        <strong>{pick('Prix client transparent', 'Transparent client price')}</strong>
        <span>{pick('Le client paiera exactement le total TTC du devis. Aucun supplément LPB ne sera ajouté.', 'The client will pay exactly the quote total including tax. No extra LPB charge will be added.')}</span>
      </ClientTransparency>

      <QuoteRevenuePreview totalTtc={quote.totalTtc} />

      {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
      <Actions>
        <SecondaryButton type="button" onClick={close} disabled={submitting || loading}>{pick('Annuler', 'Cancel')}</SecondaryButton>
        <PrimaryButton type="button" onClick={submit} disabled={submitting || loading}>{submitting ? pick('Envoi…', 'Sending…') : pick('Envoyer le devis', 'Send quote')}</PrimaryButton>
      </Actions>
    </Modal>
  );
};

const Header = styled.header`display:flex;align-items:flex-start;justify-content:space-between;gap:1rem;margin-bottom:1.2rem;min-width:0;>div{min-width:0;}@media(max-width:640px){position:sticky;top:0;z-index:3;margin:-1rem -1rem 1rem;padding:1rem;border-bottom:1px solid #eee7dc;background:rgba(255,255,255,.97);backdrop-filter:blur(12px);}`;
const Eyebrow = styled.div`margin-bottom:.35rem;color:#a88a5d;font-size:.76rem;font-weight:900;letter-spacing:.12em;text-transform:uppercase;`;
const Title = styled.h2`margin:0;color:#111;font-size:clamp(1.3rem,4vw,1.85rem);`;
const Subtitle = styled.p`margin:.4rem 0 0;color:#726b61;line-height:1.5;overflow-wrap:anywhere;`;
const CloseButton = styled.button`width:2.5rem;height:2.5rem;flex:0 0 auto;border:0;border-radius:50%;background:#f1ece5;color:#111;font:inherit;font-size:1.6rem;cursor:pointer;`;
const ProfessionalSection = styled.section`margin-bottom:1rem;padding:1rem;border:1px solid #e1d7c8;border-radius:1rem;background:#fbf8f3;`;
const SectionHeader = styled.div`display:flex;align-items:flex-start;justify-content:space-between;gap:1rem;margin-bottom:.8rem;min-width:0;>div{min-width:0;}h3{margin:0;color:#111;}p{margin:.2rem 0 0;color:#6c7983;font-size:.82rem;}@media(max-width:520px){display:grid;grid-template-columns:1fr;}`;
const TemplateLogo = styled.img`width:4.5rem;height:3.2rem;object-fit:contain;flex:0 0 auto;border-radius:.55rem;background:#fff;padding:.3rem;border:1px solid #e6ded2;`;
const Form = styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.85rem;margin-bottom:1rem;@media(max-width:580px){grid-template-columns:1fr;}`;
const Field = styled.div`grid-column:${({ $wide }) => ($wide ? '1 / -1' : 'auto')};display:grid;gap:.38rem;label{color:#222;font-weight:800;}input,textarea{width:100%;min-width:0;box-sizing:border-box;padding:.78rem;border:1px solid #ddd3c4;border-radius:.75rem;color:#111;background:#fff;font:inherit;font-size:1rem;}textarea{min-height:7rem;resize:vertical;line-height:1.5;}`;
const Hint = styled.span`justify-self:end;color:#7a8790;font-size:.76rem;`;
const ClientTransparency = styled.div`display:grid;gap:.25rem;margin-top:1rem;padding:.85rem 1rem;border-radius:.85rem;background:#edf8f1;border:1px solid #cfe4d7;strong{color:#1f6b43;}span{color:#49665a;font-size:.78rem;line-height:1.5;}`;
const ErrorMessage = styled.p`margin:.8rem 0 0;color:#a52e27;font-size:.88rem;`;
const Actions = styled.div`display:flex;justify-content:flex-end;gap:.7rem;margin-top:1.25rem;@media(max-width:520px){display:grid;grid-template-columns:1fr;}`;
const BaseButton = styled.button`min-height:2.9rem;padding:.72rem 1.05rem;border-radius:999px;font:inherit;font-weight:800;cursor:pointer;box-sizing:border-box;&:disabled{opacity:.55;cursor:wait;}@media(max-width:520px){width:100%;}`;
const PrimaryButton = styled(BaseButton)`border:1px solid #c0a375;background:#c0a375;color:#111;`;
const SecondaryButton = styled(BaseButton)`border:1px solid #e7dfd2;background:#fff;color:#111;`;

const mapStateToProps = ({ firebase, demands }) => ({ firebase, loading: demands?.loading });
const mapDispatchToProps = (dispatch) => ({ sendContractAction: (contract) => dispatch(sendContract(contract)) });
export default connect(mapStateToProps, mapDispatchToProps)(ContractModal);
